const OSM_PROXY_BASE_URL =
  "https://rowerpower-osm-proxy.rowerowy-sosnowiec.workers.dev";

const CYCLEWAYS_URL = `${OSM_PROXY_BASE_URL}/sosnowiec/cycleways`;

export const OSM_ENDPOINTS = {
  cycleways: CYCLEWAYS_URL,
  routes: `${OSM_PROXY_BASE_URL}/sosnowiec/routes`,
  bicycleParking: `${OSM_PROXY_BASE_URL}/sosnowiec/bicycle-parking`,
  repairStations: `${OSM_PROXY_BASE_URL}/sosnowiec/repair-stations`,
  compressedAir: `${OSM_PROXY_BASE_URL}/sosnowiec/compressed-air`,
  bicycleRental: `${OSM_PROXY_BASE_URL}/sosnowiec/bicycle-rental`,
  bicycleShops: `${OSM_PROXY_BASE_URL}/sosnowiec/bicycle-shops`,
} as const;

const OVERPASS_CACHE_TTL_MS = 5 * 60 * 1000;
const overpassMemoryCache = new Map<string, { expiresAt: number; data: Elements[] }>();
const inflightRequests = new Map<string, Promise<Elements[] | boolean>>();

/*
https://wiki.openstreetmap.org/wiki/Category:Pl:Rowery


DROGI
  "type": "way",
  "id": 84745437,
  "nodes": [],
  "tags": {
    "bicycle": "designated",
    "foot": "designated",
    "highway": "path",
    "segregated": "yes",
    "source": "geoportal.gov.pl:ortofoto",
    "surface": "paving_stones" / surface = asphalt
    cycleway:surface = asphalt
    foot = designated
    footway:surface = paving_stones
    highway = path
    lit = yes
    oneway:bicycle = no
    segregated = yes
    source = geoportal.gov.pl:ortofoto
    surface = paved    
  }  


Relacja: Sosnowiecki Rower Miejski (8343466)
23 człony 


SZLAKI ROWEROWE
type	route
route	bicycle
colour	green
distance	15
name	Szlak Rowerowy Czarnego Morza
network	lcn
osmc:symbol	white_rectangle:green_pointer
route	bicycle
type	route
wikidata	Q48842278
wikipedia	pl:Szlak Rowerowy Czarnego Morza


colour = #FF0000
description = Szlak rowerowy dawnego pogranicza
name = Szlak rowerowy Dawnego Pogranicza
network = lcn
osmc:symbol = white_rectangle:red_pointer
ref = 465C
route = bicycle
type = route
wikidata = Q28673022
wikipedia = pl:Szlak_Rowerowy_Dawnego_Pogranicza


https://wiki.openstreetmap.org/wiki/File:Parking-bicycle-16.svg
amenity	bicycle_parking
bicycle_parking	stands
capacity	8
https://wiki.openstreetmap.org/wiki/Pl:Tag:amenity%3Dbicycle_parking


https://wiki.openstreetmap.org/wiki/File:Bicycle_repair_station-14.svg 
amenity	bicycle_repair_station
fee	no
opening_hours	24/7
opening_hours:covid19	open
service:bicycle:repair	yes
service:bicycle:tools	yes
https://wiki.openstreetmap.org/wiki/Pl:Tag:amenity=bicycle%20repair%20station?uselang=pl

Kompresory
https://wiki.openstreetmap.org/wiki/Pl:Tag:amenity%3Dcompressed_air


Rower miejski
https://wiki.openstreetmap.org/wiki/Pl:Tag:amenity%3Dbicycle_rental
ico: nextbike albo srm
amenity: bicycle_rental
bicycle_rental: docking_station
amenity	bicycle_rental
brand	Sosnowiecki Rower Miejski
bicycle_rental	docking_station
capacity	12
name	Park Kościuszki
network	City by bike
network:wikidata	Q24944379
opening_hours	Apr-Oct
operator	Nextbike Polska
ref	5873

OVERPASS
brand:wikidata = Q55754308


Tags 11
amenity = bicycle_rental
brand = Kołobrzeski Rower Miejski
brand:wikidata = Q97373105
brand:wikipedia = pl:Kołobrzeski Rower Miejski
capacity = 12
name = Dańdówka
network = Kołobrzeski Rower Miejski
network:wikidata = Q97373105
network:wikipedia = pl:Kołobrzeski Rower Miejski
operator = Nextbike Polska
ref = 6269
Coordinates:
50.2686711 / 19.1758234 (lat/lon)





Shop
https://wiki.openstreetmap.org/wiki/Pl:Tag:shop%3Dbicycle
shop=bicycle
service:bicycle:retail=no
service:bicycle:repair=yes
service:bicycle:pump=yes
*/



export type Elements = {
  type: string;
  lat: number;
  lon: number;
  tags: Record<string, string | undefined>;
};

type OverpassElement = Elements & Record<string, any>;
type OSMEndpoint = keyof typeof OSM_ENDPOINTS;

type FetchOverpassOptions = {
  /** Selects a proxy response explicitly when a query cannot be inferred. */
  endpoint?: OSMEndpoint;
};

function getCachedOverpassResponse(cacheKey: string): Elements[] | null {
  const now = Date.now();
  const memoryHit = overpassMemoryCache.get(cacheKey);

  if (memoryHit && memoryHit.expiresAt > now) {
    return memoryHit.data;
  }

  if (memoryHit) {
    overpassMemoryCache.delete(cacheKey);
  }

  try {
    const rawValue = window.localStorage.getItem(cacheKey);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue) as { expiresAt: number; data: Elements[] };
    if (parsedValue.expiresAt <= now) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    overpassMemoryCache.set(cacheKey, parsedValue);
    return parsedValue.data;
  } catch (_error) {
    return null;
  }
}

function saveCachedOverpassResponse(cacheKey: string, data: Elements[]) {
  const payload = {
    expiresAt: Date.now() + OVERPASS_CACHE_TTL_MS,
    data,
  };

  overpassMemoryCache.set(cacheKey, payload);

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (_error) {
    // Ignore quota/storage failures. Memory cache still works for the current session.
  }
}

function resolveProxyEndpoints(query: string, endpoint?: OSMEndpoint): OSMEndpoint[] {
  if (endpoint) {
    return [endpoint];
  }

  const endpoints = new Set<OSMEndpoint>();

  if (/route\s*=\s*["']bicycle["']/.test(query)) endpoints.add("routes");
  if (/amenity\s*=\s*["']bicycle_parking["']/.test(query)) endpoints.add("bicycleParking");
  if (/amenity\s*=\s*["']bicycle_repair_station["']/.test(query)) endpoints.add("repairStations");
  if (/amenity\s*=\s*["']compressed_air["']/.test(query)) endpoints.add("compressedAir");
  if (/amenity\s*=\s*["']bicycle_rental["']/.test(query)) endpoints.add("bicycleRental");
  if (/shop\s*=\s*["']bicycle["']/.test(query)) endpoints.add("bicycleShops");

  // All currently used non-POI queries in this service describe cycling ways.
  if (endpoints.size === 0 || /\b(highway|cycleway|oneway:bicycle|living_street|tracktype)\b/.test(query)) {
    endpoints.add("cycleways");
  }

  return Array.from(endpoints);
}

async function requestOsmProxy(endpoints: OSMEndpoint[]): Promise<Elements[]> {
  const results = await Promise.allSettled(
    endpoints.map(async (endpoint) => {
      const response = await fetch(OSM_ENDPOINTS[endpoint]);

      if (!response.ok) {
        throw new Error(`OSM proxy ${response.status} on ${OSM_ENDPOINTS[endpoint]}`);
      }

      const data = await response.json();
      return (data.elements || []) as OverpassElement[];
    })
  );
  const responses = results
    .filter((result): result is PromiseFulfilledResult<OverpassElement[]> => result.status === "fulfilled")
    .map((result) => result.value);

  if (responses.length === 0) {
    const failedRequest = results.find(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    throw failedRequest?.reason instanceof Error
      ? failedRequest.reason
      : new Error("No OSM proxy endpoint is available");
  }

  const elementsById = new Map<string, Elements>();
  responses.flat().forEach((element) => {
    const id = (element as OverpassElement).id;
    const key = id === undefined ? `${element.type}:${elementsById.size}` : `${element.type}:${id}`;
    elementsById.set(key, element);
  });

  return Array.from(elementsById.values());
}

export const fetchMarkers = async (
  layerQuery: string,
  box: string | undefined | null,
  options?: FetchOverpassOptions
): Promise<Elements[] | boolean> => {
  // (${layerQuery.replace(/\[BOX\]/gi, box).replace(/\[AREA\]/gi, "(area.sosnowiec)")});
  const resolvedQuery = `
      [out:json][timeout:90];
      area[name="Sosnowiec"]->.sosnowiec;
      (${layerQuery.replace(/\[AREA\]/gi, "(area.sosnowiec)")});
      out;
      >;
      out skel qt;
    `;
  const endpoints = resolveProxyEndpoints(resolvedQuery, options?.endpoint);
  const cacheKey = `osm-proxy:${endpoints.join(",")}`;
  const cachedData = getCachedOverpassResponse(cacheKey);

  if (cachedData) {
    return cachedData;
  }

  const inflightRequest = inflightRequests.get(cacheKey);
  if (inflightRequest) {
    return inflightRequest;
  }

  try {
      const requestPromise = requestOsmProxy(endpoints)
        .then((elements) => {
          saveCachedOverpassResponse(cacheKey, elements);
          return elements;
      })
      .finally(() => {
        inflightRequests.delete(cacheKey);
      });

    inflightRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const fetchRawOverpassQuery = async (
  rawQuery: string,
  options?: FetchOverpassOptions
): Promise<Elements[] | boolean> => {
  const endpoints = resolveProxyEndpoints(rawQuery, options?.endpoint);
  const cacheKey = `osm-proxy:${endpoints.join(",")}`;
  const cachedData = getCachedOverpassResponse(cacheKey);

  if (cachedData) {
    return cachedData;
  }

  const inflightRequest = inflightRequests.get(cacheKey);
  if (inflightRequest) {
    return inflightRequest;
  }

  try {
    const requestPromise = requestOsmProxy(endpoints)
      .then((elements) => {
        saveCachedOverpassResponse(cacheKey, elements);
        return elements;
      })
      .finally(() => {
        inflightRequests.delete(cacheKey);
      });

    inflightRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  } catch (err) {
    console.error(err);
    return false;
  }
};

// https://overpass-api.de/api/interpreter?data=[out:json];%20%20%20%20%20%20node%20%20%20%20%20%20%20%20[amenity=college]%20%20%20%20%20%20%20%20(48.835474119784756,2.3644745349884033,48.874784201649106,2.407475709915161);%20%20%20%20%20%20out;
